import React, { useEffect, useCallback, useState } from 'react';

import _ from 'lodash';
import { Button, Card, CardMedia, Dialog, DialogActions } from '@material-ui/core';
import { DialogContent, Fab, FormControl, InputLabel, MenuItem } from '@material-ui/core';
import { Select, Switch, TextField, Typography } from '@material-ui/core';

import { MdAdd, MdCreate, MdDelete, MdEdit } from 'react-icons/md';
import { ImTwitter } from 'react-icons/im';

import Media from '../../Template/components/Media';
import { RenderIconButton } from '../../Channel/shared/components/GenericComponents';

import API from 'utils/api';
import { colorSort, Table, textSort } from 'components/Table';
import { ColorPicker, ConfirmDialog, DialogTitle, ErrorDialog } from 'components';
import { classes } from 'components/utils';
import TwitterDialog from './TwitterDialog';

import '../styles/TeamProfiles.css';
import styles from './TeamProfiles.module.scss';

const tableSize = {
  cellStyle: { padding: '4px 15px 4px 15px' },
  headerStyle: { padding: '4px 15px 4px 15px' },
};

const TeamProfiles = () => {
  const [team, setTeam] = useState({
    color1: 'ffffff',
    color2: 'ffffff',
    teamName: '',
    city: '',
    displayName: '',
    image: '',
    imageUrl: '',
    selectedTeam: null,
  });
  const [apis, setApis] = useState([]);
  const [teams, setTeams] = useState([]);
  const [leagueSelected, setLeagueSelected] = useState(null);
  const [errorList, setErrorList] = useState({ team: '', city: '', logo: '' });

  const [leagueSelectedAddTeam, setleagueSelectedAddTeam] = useState(null);
  const [teamSelectedAddTeam, setTeamSelectedAddTeam] = useState(null);
  const [teamsAdd, setTeamsAdd] = useState([]);

  const [dialog, setDialog] = useState({});

  useEffect(() => {
    refreshLists();
  }, [refreshLists]);

  const mediaContainer = {
    backgroundImage: `url("./images/${team.imageUrl}")`,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  };

  const changeDialogState = type => {
    setDialog({ team: type });
    setErrorList({ team: '', city: '', logo: '' });
  };

  const updateErrorMessage = (field, message) => {
    const tempErrors = errorList;
    tempErrors[field] = message;

    setErrorList({ ...tempErrors });
  };

  const refreshLists = useCallback(
    async league => {
      try {
        let firstLeague = null;
        if (league) {
          firstLeague = league;
        }
        let query = { active: true };

        await API.getLeagues({
          select: 'name provider homePosition',
          ignoreDependencies: true,
          sort: 'name',
          query: JSON.stringify(query),
        }).then(({ data }) => {
          if (data && data.docs) {
            setApis(data.docs);
            firstLeague = firstLeague
              ? firstLeague
              : data.docs.length > 0
              ? data.docs[0]
              : null;
            setLeagueSelected(firstLeague);
          }
        });

        const leagueId = firstLeague ? firstLeague._id : null;
        const teams = await getTeams(leagueId);

        setTeams(teams);
      } catch (error) {
        console.error(error);
      }
    },
    [getTeams]
  );

  const getTeams = useCallback(async leagueId => {
    const query = leagueId ? JSON.stringify({ leagues: leagueId }) : undefined;

    const { data } = await API.getTeams({
      query,
      showRealName: true,
      checkDependencies: false,
    });

    return data && data.docs ? data.docs : [];
  }, []);

  const addTeam = team => {
    if (team) {
      const teamId = team.id;
      API.editTeam(teamId, {
        appendLeague: true,
        league: leagueSelected,
      }).then(() => {
        refreshLeagues();
      });
    }
  };

  const createTeam = newTeam => {
    API.createTeam(newTeam).then(() => {
      refreshLists(leagueSelected);
    });
  };

  const editTeam = team => {
    if (team.selectedTeam && team.selectedTeam._id) {
      API.editTeam(team.selectedTeam._id, team).then(() => {
        refreshLists(leagueSelected);
      });
    } else {
      console.log('team error ');
    }
  };

  const changeLeagueAddTeam = async leagueId => {
    const teams = await getTeams(leagueId);
    setTeamsAdd(teams);
  };

  const changeLeague = leagueId => {
    let league = null;
    for (const api of apis) {
      if (api._id.toString() === leagueId.toString()) {
        league = api;
        break;
      }
    }
    refreshLists(league);
  };

  const refreshLeagues = () => {
    refreshLists(leagueSelected ? leagueSelected : null);
  };

  const deleteTeam = team => {
    const multiLeagues = team && team.leagues && team.leagues.length > 1;
    if (multiLeagues) {
      API.editTeam(team._id, {
        removeLeague: true,
        league: leagueSelected,
      }).then(() => {
        refreshLeagues();
      });
    } else {
      API.deleteTeam(team._id).then(() => {
        refreshLeagues();
      });
    }
  };

  const confirmDeleteTeam = row => {
    let message = `Do you confirm to completely delete ${row.name} from the system?`;

    if (row && row.leagues && row.leagues.length > 1) {
      message = `Do you confirm removing ${row.name} from the selected league?`;
    }

    setDialog({ delete: { title: 'Delete team', message, team: row } });
  };

  const cleanForm = () => {
    setTeam({
      color1: 'ffffff',
      color2: 'ffffff',
      teamName: '',
      city: '',
      displayName: '',
      image: '',
      imageUrl: '',
      selectedTeam: '',
    });
  };

  const invert_img = (...args) => {
    const rgb = [].slice
      .call(args)
      .join(',')
      .replace(/rgb\(|\)|rgba\(|\)|\s/gi, '')
      .split(',');

    for (var counter = 0; counter < rgb.length; counter++)
      rgb[counter] = (counter === 3 ? 1 : 255) - rgb[counter];

    return `rgb(${rgb.join(', ')})`;
  };

  const hexToRgb = hex => {
    var result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? invert_img(
          `rgb(${parseInt(result[1], 16)},${parseInt(result[2], 16)},${parseInt(
            result[3],
            16
          )})`
        )
      : null;
  };

  const updateImageToSend = (image, imageUrl) => {
    setTeam({ ...team, image, imageUrl });
  };

  const fillDialogForm = row => {
    const color1 = row.colors && row.colors.length > 0 ? row.colors[0] : '#ffffff';
    const color2 = row.colors && row.colors.length > 1 ? row.colors[1] : '#ffffff';

    setTeam({
      color1,
      color2,
      teamName: row.name,
      city: row.city ? row.city : '',
      displayName: row.displayName || '',
      imageUrl: row.displayLogo && row.displayLogo.url ? row.displayLogo.url : '',
      selectedTeam: row,
    });
  };

  const renderSelect = (disabled, value, title) => {
    return (
      <FormControl className={styles.formControlSelect}>
        <InputLabel id="demo-simple-select-label">{title}</InputLabel>
        <Select
          id="demo-simple-select"
          disabled={disabled}
          value={value}
          onChange={event => {
            changeLeague(event.target.value);
          }}
        >
          {apis.map(row => (
            <MenuItem key={row._id} value={row._id}>
              <div className="select">{row.name}</div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    );
  };

  const renderTable = () => {
    return (
      <div className={styles.table}>
        <Table
          columns={[
            {
              ...tableSize,
              title: 'Logo',
              field: 'logo',
              render: rowData => {
                const logo = _.get(rowData, 'logo.url', '');
                return (
                  <React.Fragment>
                    {
                      <Card className={styles.card}>
                        <CardMedia
                          className={styles.cardMedia}
                          style={{
                            backgroundImage: `url(${logo}) `,
                          }}
                          image={`url('${logo}')`}
                        />
                      </Card>
                    }
                  </React.Fragment>
                );
              },
              sorting: false,
            },
            {
              ...tableSize,
              title: 'DisplayLogo',
              field: 'displayLogo',
              render: rowData => {
                const displayLogo = _.get(rowData, 'displayLogo.url', '');
                return (
                  <React.Fragment>
                    {
                      <Card className={styles.card}>
                        <CardMedia
                          className={styles.cardMedia}
                          style={{
                            backgroundImage: `url(${displayLogo}) `,
                          }}
                          image={`url('${displayLogo}')`}
                        />
                      </Card>
                    }
                  </React.Fragment>
                );
              },
              sorting: false,
            },
            {
              ...tableSize,
              title: 'Name',
              field: 'name',
              customSort: textSort('name'),
              defaultSort: 'asc',
            },
            {
              ...tableSize,
              title: 'City',
              field: 'city',
              customSort: textSort('city'),
            },
            {
              ...tableSize,
              title: 'Display Name',
              field: 'displayName',
              customSort: textSort('displayName'),
            },
            {
              ...tableSize,
              title: 'Primary Color',
              field: 'color1',
              customSort: colorSort(0),
              render: rowData => {
                const color1 = _.get(rowData, 'colors.0', 'ffffff');

                return (
                  <span
                    className={styles.teamColor}
                    style={
                      color1 && hexToRgb(color1)
                        ? {
                            color: hexToRgb(color1),
                            backgroundColor: `#${color1}`,
                          }
                        : {}
                    }
                  >
                    {'#' + color1}
                  </span>
                );
              },
            },
            {
              ...tableSize,
              title: 'Secondary Color',
              field: 'color2',
              customSort: colorSort(1),
              render: rowData => {
                const color2 = _.get(rowData, 'colors.1', 'ffffff');
                return (
                  <span
                    className={styles.teamColor}
                    style={
                      color2 && hexToRgb(color2)
                        ? {
                            color: hexToRgb(color2),
                            backgroundColor: `#${color2}`,
                          }
                        : {}
                    }
                  >
                    {'#' + color2}
                  </span>
                );
              },
            },
            {
              ...tableSize,
              title: 'Active',
              field: 'active',
              render: rowData => (
                <div>
                  <Switch
                    color="primary"
                    checked={Boolean(!rowData.hide)}
                    onChange={() => {
                      if (rowData.hasEvent) {
                        setDialog({ alert: true });
                      } else {
                        const newTeam = {
                          league: leagueSelected,
                          selectedTeam: { _id: rowData._id },
                          hide: !rowData.hide,
                        };

                        editTeam(newTeam);
                      }
                    }}
                  />
                </div>
              ),
              sorting: false,
            },
            {
              ...tableSize,
              title: 'Services',
              render: rowData => (
                <div>
                  <RenderIconButton
                    disabled={!rowData.twitterServiceAvailable}
                    clickEvent={() => {
                      setDialog({
                        twitter: { teamId: rowData._id },
                      });
                    }}
                    title={'Tweets service'}
                    placement={'top'}
                    icon={<ImTwitter />}
                    color={!rowData.hasTwitterAccounts ? 'primary' : 'secondary'}
                  />
                </div>
              ),
              sorting: false,
            },
            {
              ...tableSize,
              title: 'Options',
              render: rowData => (
                <div>
                  <React.Fragment>
                    {/*<RenderIconButton
                      disabled={rowData.hasDependencies}
                      clickEvent={() => {
                        confirmDeleteTeam(rowData);
                      }}
                      title={'Delete Team'}
                      placement={'top'}
                      icon={<MdDelete />}
                      color={'primary'}
                    />
                    */}
                    <RenderIconButton
                      clickEvent={() => {
                        fillDialogForm(rowData);
                        changeDialogState('edit');
                      }}
                      title={'Edit Team'}
                      placement={'top'}
                      icon={<MdEdit />}
                      color={'primary'}
                    />
                  </React.Fragment>
                </div>
              ),
              sorting: false,
            },
          ]}
          data={teams}
          title={'Team Profiles'}
        />
      </div>
    );
  };

  const textFieldProps = (name, label) => ({
    name,
    label,
    id: `standard-full-width-${name}`,
    inputProps: { maxLength: 100 },
    InputLabelProps: { shrink: true },
    fullWidth: true,
  });

  const dialogContentTeam = (
    <React.Fragment>
      <div className={styles.left}>
        <TextField
          {...textFieldProps('teamName', 'Team Name')}
          className={styles.textField}
          placeholder="Enter Team name"
          value={team.teamName}
          onChange={event => setTeam({ ...team, teamName: event.target.value })}
          {...(errorList.team ? { error: true, helperText: errorList.team } : {})}
          required
        />

        <TextField
          {...textFieldProps('city', 'City Name')}
          className={styles.textField}
          placeholder="Enter the city name"
          value={team.city}
          onChange={event => setTeam({ ...team, city: event.target.value })}
          {...(errorList.city ? { error: true, helperText: errorList.city } : {})}
          required
        />

        <TextField
          {...textFieldProps('displayName', 'Display Name')}
          className={styles.textField}
          placeholder="Enter the name to be shown in the UI"
          value={team.displayName}
          onChange={event => setTeam({ ...team, displayName: event.target.value })}
        />

        <div className={styles.teamColor}>
          <div className={styles.teamColorSub}>
            <div className={styles.teamColorTitle}>{'Primary Color'}</div>
            <ColorPicker
              theme="round"
              color={team.color1}
              onChange={color => setTeam({ ...team, color1: color })}
              returnType="hex"
            />
          </div>
          <div className={styles.teamColorSub}>
            <div className={styles.teamColorTitle}>{'Secondary Color'}</div>
            <ColorPicker
              theme="round"
              color={team.color2}
              onChange={color => setTeam({ ...team, color2: color })}
              returnType="hex"
            />
          </div>
        </div>
      </div>
      <div className={styles.right}>
        <Typography
          variant="subtitle2"
          gutterBottom
          align="center"
          style={errorList.logo ? { color: '#f44336' } : {}}
        >
          Team Logo*
        </Typography>
        <div
          className={
            team.imageUrl !== ''
              ? classes(
                  mediaContainer,
                  ' icon-container media-container',
                  styles.logoImage
                )
              : classes('icon-container media-container', styles.logoImage)
          }
        >
          <Media
            size={200}
            mediaType="image"
            mediaFile={team.image}
            mediaUrl={team.imageUrl}
            onChange={updateImageToSend}
            onFetch={updateImageToSend}
          />
        </div>

        {errorList.logo ? <span className={styles.logoError}>{errorList.logo}</span> : ''}
      </div>
    </React.Fragment>
  );

  const dialogContentAddTeam = (
    <React.Fragment>
      <div className={styles.header}>
        <FormControl className={styles.formControlSelect}>
          <InputLabel id="demo-simple-select-label">League</InputLabel>
          <Select
            id="demo-simple-select"
            value={leagueSelectedAddTeam ? leagueSelectedAddTeam : ''}
            onChange={event => {
              changeLeagueAddTeam(event.target.value);
              setleagueSelectedAddTeam(event.target.value);
            }}
          >
            {apis.map(row => (
              <MenuItem key={row._id} value={row._id}>
                <div className="select">{row.name}</div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl className={styles.formControlSelect}>
          <InputLabel id="demo-simple-select-label">Team</InputLabel>
          <Select
            id="demo-simple-select"
            value={teamSelectedAddTeam ? teamSelectedAddTeam : ''}
            onChange={event => {
              setTeamSelectedAddTeam(event.target.value);
            }}
          >
            {teamsAdd.map(row => (
              <MenuItem key={row._id} value={row}>
                <div className="select">{row.name}</div>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className={styles.left}>
        <TextField
          {...textFieldProps('teamName', 'Team Name')}
          className={styles.textField}
          value={teamSelectedAddTeam ? teamSelectedAddTeam.name : ''}
          disabled={true}
        />
        <TextField
          {...textFieldProps('city', 'City Name')}
          className={styles.textField}
          value={teamSelectedAddTeam ? teamSelectedAddTeam.city : ''}
          disabled={true}
        />
        <TextField
          {...textFieldProps('displayName', 'Display Name')}
          className={styles.textField}
          value={
            teamSelectedAddTeam && teamSelectedAddTeam.display
              ? teamSelectedAddTeam.displayName
              : ''
          }
          disabled={true}
        />
      </div>
      <div className={styles.right}>
        <Typography
          variant="subtitle2"
          gutterBottom
          align="center"
          style={errorList.logo ? { color: '#f44336' } : {}}
        >
          Team Logo
        </Typography>
        <div
          className={
            team.imageUrl !== ''
              ? classes(
                  mediaContainer,
                  ' icon-container media-container',
                  styles.logoImage
                )
              : classes('icon-container media-container', styles.logoImage)
          }
        >
          <img
            src={
              teamSelectedAddTeam &&
              teamSelectedAddTeam.displayLogo &&
              teamSelectedAddTeam.displayLogo.url
                ? teamSelectedAddTeam.displayLogo.url
                : null
            }
            alt=""
          />
        </div>
      </div>
    </React.Fragment>
  );

  const onTeamDialogClose = () => {
    changeDialogState(false);
    cleanForm();
  };

  const onTeamDialogSave = () => {
    let errorCount = 0;

    if (!team.teamName || (team.teamName && team.teamName === '')) {
      updateErrorMessage('team', 'Team name has to be filled');
      errorCount++;
    }

    if (!team.city || (team.city && team.city === '')) {
      updateErrorMessage('city', 'City has to be filled');
      errorCount++;
    }

    if (!team.image || (team.image && team.image === '')) {
      if (!team.imageUrl || (team.imageUrl && team.imageUrl === '')) {
        updateErrorMessage('logo', 'The team needs a Logo');
        errorCount++;
      }
    }

    if (errorCount === 0) {
      const newTeam = {
        name: team.teamName || undefined,
        city: team.city || undefined,
        displayName:
          team.displayName && team.displayName.length ? team.displayName : undefined,
        image: team.image,
        colors: [team.color1, team.color2],
        league:
          leagueSelected && leagueSelected._id ? leagueSelected._id.toString() : null,
      };

      if (dialog.team === 'edit') {
        newTeam.selectedTeam = team.selectedTeam;
        editTeam(newTeam);
      } else {
        createTeam(newTeam);
      }

      cleanForm();
      changeDialogState(false);
    }
  };

  return (
    <div className={styles.teamProfiles}>
      <div className={styles.header}>
        <div className={styles.selectContainer}>
          {renderSelect(false, leagueSelected ? leagueSelected._id : '', 'League')}
        </div>

        <div className={styles.buttonsContainer}>
          <Fab
            variant="extended"
            size="small"
            color="primary"
            aria-label="create team"
            onClick={() => {
              changeDialogState('create');
            }}
            className={styles.createButton}
          >
            <MdCreate className={styles.icon} />
            Create new Team
          </Fab>
          <Fab
            variant="extended"
            size="small"
            color="primary"
            aria-label="add team"
            onClick={() => {
              setDialog({ addExistingTeam: true });
            }}
            className={styles.addButton}
          >
            <MdAdd className={styles.icon} />
            Add team
          </Fab>
        </div>
      </div>

      {renderTable()}

      <Dialog
        className={styles.dialogTeam}
        open={!!dialog.team}
        onClose={onTeamDialogClose}
        fullWidth
      >
        <DialogTitle onClose={onTeamDialogClose}>
          {dialog.team === 'edit' ? 'Edit Team' : 'Add Team'}
        </DialogTitle>

        <DialogContent>{dialogContentTeam}</DialogContent>

        <DialogActions>
          <Button onClick={onTeamDialogClose} color="primary">
            cancel
          </Button>
          <Button onClick={onTeamDialogSave} color="primary">
            Save Team
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        className={styles.dialogAddTeam}
        open={!!dialog.addExistingTeam}
        onClose={() => setDialog({})}
        fullWidth
      >
        <DialogTitle onClose={() => setDialog({})}>Add team</DialogTitle>
        <DialogContent>{dialogContentAddTeam}</DialogContent>

        <DialogActions>
          <Button
            onClick={() => {
              setDialog({});
              setTeamSelectedAddTeam(null);
              setleagueSelectedAddTeam(null);
            }}
            color="primary"
          >
            cancel
          </Button>

          <Button
            onClick={() => {
              setDialog({});
              addTeam(teamSelectedAddTeam);
            }}
            color="primary"
          >
            Add Team
          </Button>
        </DialogActions>
      </Dialog>

      <TwitterDialog
        open={!!dialog.twitter}
        onClose={() => {
          setDialog({});
          refreshLists(leagueSelected);
        }}
        teamId={dialog.twitter ? dialog.twitter.teamId : null}
      />

      <ConfirmDialog
        open={!!dialog.delete}
        onClose={yes => {
          setDialog({});
          const team = _.get(dialog, 'delete.team', null);

          if (yes && team) {
            deleteTeam(team);
          }
        }}
        title={dialog.delete ? dialog.delete.title : ''}
        message={dialog.delete ? dialog.delete.message : ''}
      />

      <ErrorDialog
        open={!!dialog.alert}
        onClose={() => setDialog({})}
        title="You cannot disable this team."
        message="There is one or more games associated with this team."
      />
    </div>
  );
};

export default TeamProfiles;
